/**
 * หน้าจอ "เข้าไม่ได้" ทั้งหมดของ Desktop shell
 *
 * ทุกหน้าที่นี่ตั้งใจให้อ่านรู้เรื่องและจบในตัว ไม่ใช่ error dump และไม่ใช่ spinner
 * ที่หมุนค้าง — ผู้ใช้ต้องรู้ว่าเกิดอะไรขึ้นและต้องทำอะไรต่อ
 *
 * ⚠️ การปฏิเสธที่นี่เป็น **UX เท่านั้น** ผู้ปฏิเสธจริงคือ backend ทุก request
 * (`references/authorization-rbac.md` ข้อ 5) หน้าจอนี้แค่ช่วยไม่ให้ผู้ใช้เดินเข้า
 * ทางตันโดยไม่รู้ตัว
 */

import { Link } from 'react-router-dom';

import { StatusBadge } from './StatusBadge';
import { availabilityLabel } from './navigation';
import type { DesktopModuleRoute } from './routes';
import { describePermissionsState } from '../lib/permissions/client';
import type { PermissionsState } from '../lib/permissions/types';

const UX_ONLY_NOTE =
  'การซ่อน/ปิดหน้าจอเป็นเพียง UX — การบังคับสิทธิ์จริงอยู่ที่ backend ทุกคำขอเสมอ';

function BackHomeLink() {
  return (
    <Link className="cap-button cap-button--ghost" to="/">
      กลับหน้าภาพรวมระบบ
    </Link>
  );
}

/**
 * พาดหัวของการปฏิเสธ แยกตามเหตุผลจริง เพื่อไม่ให้ "ยังตอบไม่มา" ถูกอ่านว่า
 * "คุณไม่มีสิทธิ์" — ทั้งสองกรณีปิดหน้าจอเหมือนกัน แต่สิ่งที่ผู้ใช้ต้องทำต่างกัน
 *
 * แจกแจงครบทุกสถานะโดยไม่มี `default` เพื่อให้ TypeScript พิสูจน์ความครบถ้วน
 */
function refusalHeading(state: PermissionsState): string {
  switch (state.status) {
    case 'ready':
      return 'ไม่มีสิทธิ์เข้าถึงหน้าจอนี้';
    case 'loading':
      return 'กำลังตรวจสอบสิทธิ์ก่อนเปิดหน้าจอนี้';
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะเปิดหน้าจอนี้ได้';
    case 'forbidden':
    case 'network-error':
    case 'server-error':
    case 'invalid-response':
    case 'cancelled':
      return 'ยืนยันสิทธิ์ไม่สำเร็จ จึงยังไม่เปิดหน้าจอนี้';
  }
}

export interface NotAuthorisedViewProps {
  readonly permissions: PermissionsState;
  readonly route: DesktopModuleRoute;
}

/**
 * ปลายทางของการพิมพ์ URL ตรงไปยังหน้าจอที่ไม่มีสิทธิ์
 *
 * ต้องไม่ใช่หน้าขาวและต้องไม่ใช่ตัวหน้าจอจริง — นี่คือกรณีที่ Work Order นี้มีไว้
 * เพื่อป้องกัน: ซ่อนลิงก์แล้วแต่ปล่อย route เปิดทิ้งไว้
 */
export function NotAuthorisedView({ permissions, route }: NotAuthorisedViewProps) {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="not-authorised" role="status">
        <StatusBadge tone="warning">เข้าถึงไม่ได้</StatusBadge>
        <h1 className="cap-notice__title">{refusalHeading(permissions)}</h1>
        <p className="cap-notice__text">
          หน้าจอ “{route.label}” ต้องการสิทธิ์ {route.action} ของโมดูล{' '}
          <code className="cap-table__code">{route.moduleCode}</code>
        </p>
        <p className="cap-notice__text">{describePermissionsState(permissions)}</p>
        <p className="cap-notice__note">{UX_ONLY_NOTE}</p>
        <div className="cap-notice__actions">
          <BackHomeLink />
        </div>
      </div>
    </div>
  );
}

export interface ModuleNotAvailableViewProps {
  readonly route: DesktopModuleRoute;
}

/**
 * มีสิทธิ์ แต่หน้าจอยังไม่ถูกสร้างในบิลด์นี้
 *
 * จงใจให้ "ว่างและบอกตรง ๆ" ไม่ใส่ตาราง ปุ่มบันทึก หรือข้อมูลตัวอย่างใด ๆ เพราะ
 * `docs/SCOPE.md` ห้ามทำให้หน้าจอที่ยังไม่มีธุรกรรมจริงดูเหมือนใช้งานได้
 */
export function ModuleNotAvailableView({ route }: ModuleNotAvailableViewProps) {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="module-not-available" role="status">
        <StatusBadge tone={route.availability === 'phase-2' ? 'neutral' : 'planned'}>
          {availabilityLabel(route.availability)}
        </StatusBadge>
        <h1 className="cap-notice__title">{route.label}</h1>
        <p className="cap-notice__text">
          คุณมีสิทธิ์ {route.action} ของโมดูล{' '}
          <code className="cap-table__code">{route.moduleCode}</code> แล้ว
          แต่หน้าจอนี้ยังไม่เปิดใช้งานในบิลด์นี้ จึงยังบันทึกธุรกรรมใด ๆ ไม่ได้
        </p>
        {route.note === undefined ? null : (
          <p className="cap-notice__note">อ้างอิงแผนงาน: {route.note}</p>
        )}
        <div className="cap-notice__actions">
          <BackHomeLink />
        </div>
      </div>
    </div>
  );
}

/*
 * `SignInRequiredView` ถูกถอดออกใน `WO-P1-M10-003`
 *
 * มันเคยเป็นหน้าที่บอกว่า "เฟส 1 ยังไม่เปิดหน้าเข้าสู่ระบบ" ซึ่งไม่จริงอีกต่อไป และ
 * สถานะที่มันเคยรองรับก็ไปไม่ถึงแล้ว: `401` จาก endpoint ใดก็ตามล้าง token ทิ้งทันที
 * (D-049) แล้ว `App` สลับไปเรนเดอร์หน้าเข้าสู่ระบบให้เอง คำตอบที่ถูกต้องของ
 * "ยังไม่ได้เข้าสู่ระบบ" คือฟอร์มเข้าสู่ระบบ ไม่ใช่หน้าที่อธิบายว่าทำไมถึงยังไม่มีฟอร์ม
 */
